import React from 'react';
import { DrawerV2 } from '@withjoy/joykit';
import type { ProductListV2, InCartProduct } from './ShoppingCart.types';
import { CartCheckoutSteps } from './ShoppingCart.types';
import { ShoppingCartInnerV2 } from './ShoppingCartInnerV2';
import type { RegistryOrderFragment } from '@graphql/fragments/registry.graphql.generated';
import type { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { useShoppingCart } from './state';
import { useMediaQuery } from '@withjoy/joykit/utils';

interface ShoppingCartDialogProps
  extends Readonly<{
    isOpen: boolean;
    eventHandle: string;
    eventId: string;
    helpDialogOpen: boolean;
    isAddExternalOrderTrackingDialogOpen: boolean;
    productList: ProductListV2;
    reservedOrderList: RegistryOrderFragment[];
    purchasedOrderList: RegistryOrderFragment[];
    orderCount: number;
    cartOrderCount: number;
    cartTotal: string;
    orderForTrackingRequest: Maybe<RegistryOrderFragment>;
    paths: ReturnType<typeof useRoutePaths>;
    inCartProductList: Array<InCartProduct>;
    inCartProductCount: number;
    inCartProductTotal: number;
    showHelpDialog: () => void;
    handleDialogClose: () => void;
    closeAddExternalOrderTrackingDialog: () => void;
    registryCurrencyCode?: string;
  }> {}

export const ShoppingCartDialogV2: React.FC<ShoppingCartDialogProps> = ({
  isOpen,
  eventHandle,
  eventId,
  helpDialogOpen,
  isAddExternalOrderTrackingDialogOpen,
  productList,
  reservedOrderList,
  purchasedOrderList,
  orderCount,
  cartOrderCount,
  cartTotal,
  orderForTrackingRequest,
  inCartProductList,
  inCartProductCount,
  inCartProductTotal,
  paths,
  showHelpDialog,
  handleDialogClose,
  closeAddExternalOrderTrackingDialog,
  registryCurrencyCode,
}) => {
  const {
    state: { currentCheckoutStep: currentStep },
    mutators: { closeShoppingCart },
  } = useShoppingCart();
  const isBelowXs = useMediaQuery(theme => theme.mediaQueries.down({ breakpointAlias: 'xs' }));
  const cartSize = isBelowXs ? 1 : 374;
  return (
    <DrawerV2
      isOpen={isOpen}
      onClose={closeShoppingCart}
      anchor="right"
      size={currentStep === CartCheckoutSteps.Cart ? cartSize : 1}
      overrides={{
        Content: { props: { maxWidth: '100vw' } },
      }}
    >
      <DrawerV2.Body
        paddingX={0}
        paddingY={0}
        display="flex"
        flexDirection="column"
      >
        <ShoppingCartInnerV2
          eventId={eventId}
          eventHandle={eventHandle}
          helpDialogOpen={helpDialogOpen}
          isAddExternalOrderTrackingDialogOpen={isAddExternalOrderTrackingDialogOpen}
          productList={productList}
          reservedOrderList={reservedOrderList}
          purchasedOrderList={purchasedOrderList}
          orderCount={orderCount}
          cartOrderCount={cartOrderCount}
          cartTotal={cartTotal}
          orderForTrackingRequest={orderForTrackingRequest}
          paths={paths}
          inCartProductList={inCartProductList}
          inCartProductCount={inCartProductCount}
          inCartProductTotal={inCartProductTotal}
          showHelpDialog={showHelpDialog}
          handleDialogClose={handleDialogClose}
          closeAddExternalOrderTrackingDialog={closeAddExternalOrderTrackingDialog}
          registryCurrencyCode={registryCurrencyCode}
        />
      </DrawerV2.Body>
    </DrawerV2>
  );
};

ShoppingCartDialogV2.displayName = 'ShoppingCartDialogV2';
